import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CreateSsoUser,
  CreateSsoUserRequest,
  DeleteSsoUser,
  DeleteSsoUserRequest,
  GetSsoUsersByTenant,
  UpdateSsoUser,
  UpdateSsoUserRequest,
} from 'src/api/cqgaccounts'
import { useCompanyContext } from 'src/companies/routes/company-context'
import usePaginatedQuery from 'src/utils/microservice/paginated-query'

const DEFAULT_PAGE_SIZE = 50

export const FIND_ALL_SSO_USERS_FOR_TENANT_QUERY_KEY = 'find-all-sso-users-for-tenant'

export function useFindAllSsoUsersForTenant() {
  const { company } = useCompanyContext()

  return usePaginatedQuery({
    queryKey: [FIND_ALL_SSO_USERS_FOR_TENANT_QUERY_KEY, company.tenant_id],
    pageSize: DEFAULT_PAGE_SIZE,
    queryFn: async ({ continuationToken, pageSize }) => {
      // Adapting the generic 'continuationToken' to act as our integer 'skip' offset.
      // The next offset is calculated by adding the current payload length to the previous skip.
      // CQG Accounts does not read the continuation token, so we use 'skip' as the offset.
      const skip = Number(continuationToken ?? 0)
      const [err, response] = await GetSsoUsersByTenant({
        tenantId: company.tenant_id,
        skip,
        take: pageSize,
      })

      if (err) throw err

      return {
        ...response,
        continuationToken: skip + (response?.data?.length ?? 0),
      }
    },
  })
}

export function useCreateSsoUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateSsoUserRequest) => {
      const [err, response] = await CreateSsoUser(data)
      if (err) throw err

      await queryClient.invalidateQueries({
        queryKey: [FIND_ALL_SSO_USERS_FOR_TENANT_QUERY_KEY],
      })

      return response?.data
    },
  })
}

export function useUpdateSsoUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateSsoUserRequest) => {
      const [err, response] = await UpdateSsoUser(data)
      if (err) throw err

      await queryClient.invalidateQueries({
        queryKey: [FIND_ALL_SSO_USERS_FOR_TENANT_QUERY_KEY],
      })

      return response?.data
    },
  })
}

export function useDeleteSsoUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: DeleteSsoUserRequest) => {
      const [err, response] = await DeleteSsoUser(data)
      if (err) throw err

      await queryClient.invalidateQueries({
        queryKey: [FIND_ALL_SSO_USERS_FOR_TENANT_QUERY_KEY],
      })

      return response
    },
  })
}
